import * as Headless from "@headlessui/react";
import clsx from "clsx";
import React from "react";
import { Divider } from "src/components/base/divider";

export function Fieldset({
    className,
    ...props
}: { className?: string } & Omit<Headless.FieldsetProps, "as" | "className">) {
    return (
        <Headless.Fieldset
            {...props}
            className={clsx(className, "*:data-[slot=text]:mt-1 [&>*+[data-slot=control]]:mt-6")}
        />
    );
}

export function Legend({
    className,
    ...props
}: { className?: string } & Omit<Headless.LegendProps, "as" | "className">) {
    return (
        <Headless.Legend
            data-slot="legend"
            {...props}
            className={clsx(
                className,
                "data-disabled:opacity-50 text-base/6 font-semibold text-zinc-950 sm:text-sm/6 dark:text-white",
            )}
        />
    );
}

export function FieldGroup({ className, ...props }: React.ComponentPropsWithoutRef<"div">) {
    return <div data-slot="control" {...props} className={clsx(className, "space-y-8")} />;
}

export function Field({ className, ...props }: { className?: string } & Omit<Headless.FieldProps, "as" | "className">) {
    return (
        <Headless.Field
            {...props}
            className={clsx(
                className,
                "[&>[data-slot=label]+[data-slot=control]]:mt-3",
                "[&>[data-slot=label]+[data-slot=description]]:mt-1",
                "[&>[data-slot=description]+[data-slot=control]]:mt-3",
                "[&>[data-slot=control]+[data-slot=description]]:mt-3",
                "[&>[data-slot=control]+[data-slot=error]]:mt-3",
                "*:data-[slot=label]:font-medium",
            )}
        />
    );
}

/**
 * Use {@link HorizontalFieldDivider} to separate the left from the right half:
 *
 * ```tsx
 * <HorizontalField>
 *     I'm on the left
 *     <HorizontalFieldDivider />
 *     I'm on the right
 * </HorizontalField>
 * ```
 */
export function HorizontalField(
    props: { className?: string; children?: React.ReactNode } & Omit<
        Headless.FieldProps,
        "as" | "className" | "children"
    >,
) {
    const { className, children, ...passThrough } = props;

    const left: Array<React.ReactNode> = [];
    const right: Array<React.ReactNode> = [];
    let seenDivider = false;
    React.Children.forEach(children, (child) => {
        if (seenDivider) {
            right.push(child);
        } else if (
            typeof child === "object" &&
            child !== null &&
            "type" in child &&
            child?.type === HorizontalFieldDivider
        ) {
            seenDivider = true;
        } else {
            left.push(child);
        }
    });

    return (
        <Headless.Field
            {...passThrough}
            className={clsx(className, "grid grid-cols-1 gap-x-16 max-lg:gap-y-3 lg:grid-cols-[2fr_3fr]")}
        >
            <div
                className={clsx(
                    "*:data-[slot=label]:font-medium",
                    "[&>[data-slot=label]+[data-slot=control]]:mt-3",
                    "[&>[data-slot=label]+[data-slot=description]]:mt-1",
                    "[&>[data-slot=description]+[data-slot=control]]:mt-3",
                    "[&>[data-slot=control]+[data-slot=description]]:mt-3",
                    "*:data-[slot=label]:font-medium",
                )}
            >
                {left}
            </div>
            <div className={clsx("[&>[data-slot=control]+[data-slot=error]]:mt-3")}>{right}</div>
        </Headless.Field>
    );
}

/** Utility for {@link HorizontalField} */
export function HorizontalFieldDivider(): React.ReactNode {
    throw new Error("HorizontalFieldDivider should only be rendered once in a HorizontalField");
}

export function Label({ className, ...props }: { className?: string } & Omit<Headless.LabelProps, "className">) {
    return (
        <Headless.Label
            data-slot="label"
            {...props}
            className={clsx(
                className,
                "select-none text-base/6 text-zinc-950 data-[disabled]:opacity-50 sm:text-sm/6 dark:text-white",
            )}
        />
    );
}

export function RequiredLabel({
    className,
    ...props
}: {
    className?: string;
} & Omit<Headless.LabelProps, "className">) {
    return (
        <Headless.Label
            data-slot="label"
            {...props}
            className={clsx(
                className,
                "data-disabled:opacity-50 select-none text-base/6 text-zinc-950 sm:text-sm/6 dark:text-white",
                // asterics
                "after:ml-1 after:text-red-500 after:content-['*']",
            )}
        />
    );
}

export function Description({
    className,
    ...props
}: { className?: string } & Omit<Headless.DescriptionProps, "as" | "className">) {
    return (
        <Headless.Description
            data-slot="description"
            {...props}
            className={clsx(
                className,
                "data-disabled:opacity-50 text-base/6 text-zinc-500 sm:text-sm/6 dark:text-zinc-400",
            )}
        />
    );
}

export function ErrorMessage({
    className,
    ...props
}: { className?: string } & Omit<Headless.DescriptionProps, "as" | "className">) {
    return (
        <Headless.Description
            data-slot="error"
            {...props}
            className={clsx(
                className,
                "data-disabled:opacity-50 text-base/6 text-red-600 sm:text-sm/6 dark:text-red-500",
            )}
        />
    );
}
